/* eslint-disable no-param-reassign */
import {
  ContainerGetEmployerQuery,
  ContainerGetForeignNationalQuery,
  ContainerGetTeamMemberQuery,
  CreateEmployerInput,
  CreateForeignNationalInput,
  CreateTeamMemberInput,
  FamilyRelationship,
  FileRequirement,
  FileType,
  ForeignNational,
  GetEmployerSnapshotCountQuery,
  GetForeignNationalSnapshotCountQuery,
  GetTeamMemberSnapshotCountQuery,
  ListTasksQuery,
  PeopleByEmailQuery,
  PeopleBySlugQuery,
  PersonType,
  Position,
} from 'API';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Link } from 'react-router-dom';
import * as queries from 'graphql/queries';
import * as customQueries from 'graphql/customQueries';
import {
  getEmployer,
  getForeignNational,
  getTeamMember,
  updateEmployer,
  updateForeignNational,
  updateTeamMember,
} from 'graphql/customPersonContainerQueries';
import AuthService from 'services/auth';
import NationalService from 'services/national';
import { FileCategory, Role } from 'models';
import labels from 'shared/labels/personLabels';
import slugify from 'slugify';
import { DICTIONARY, FILE_TYPES, SLUGIFY_OPTIONS } from 'shared/constants';
import { ROUTES } from 'shared/routes';
import {
  getCacheWithExpiration,
  lower, notNullFilter, possessive, uniquesByKey, nationalFileRequirements, isRelativeAdmin,
} from 'shared/utilities';
import {
  customListTaskQueryForCount,
  getEmployerSnapshotCount,
  getForeignNationalSnapshotCount,
  getTeamMemberSnapshotCount,
  personTaskCountQuery,
} from 'graphql/customQueries';
import DocumentService from 'services/document';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

interface DocumentType {
  type: FileType;
  category: FileCategory;
  instructions?: React.ReactNode
  defaultStorage?: 'company' | 'national' | 'processRequest' | 'case';
  visibility?: string[]
}

const {
  fileRequirementsMatchingCategory,
} = DocumentService;

type CreatePersonInput = CreateForeignNationalInput
| CreateEmployerInput
| CreateTeamMemberInput;

type PersonTaskCountQueryResult = {
  [getPersonField: string]: {
    tasks?: {
      items?: Array<{ id: string } | null> | null;
      nextToken?: string | null;
    } | null;
  } | null;
};

class PersonService {
  private createPersonAPI = {
    apiName: 'AdminQueries',
    path: '/createPerson',
  };

  private personID = '';

  private person:Person | null = null;

  private personType: PersonType | undefined;

  private getPayload = async (body: any) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() ?? '';

    return {
      body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    };
  };

  public create = async (
    input: CreatePersonInput,
    role: Role,
    companyID?: string | null,
  ): Promise<Person> => {
    const { lowerFirst, lowerLast, lowerFull } = this.getLowerNames(input);

    try {
      const { body } = await post({ apiName: this.createPersonAPI.apiName, path: this.createPersonAPI.path, options: await this.getPayload({
        ...input,
        lowerFirst,
        lowerLast,
        lowerFull,
        role,
        ...(companyID && { companyID }),
      }) }).response;

      return body.json() as unknown as Person;
    } catch (err: any) {
      throw new Error(err?.response?.data?.message || 'Unknown error');
    }
  };

  public isEmailTaken = async (
    email: string,
  ): Promise<boolean> => Boolean(await this.getByEmail(email));

  public getByEmail = async (email: string): Promise<Person | null> => {
    try {
      let query;

      const user = await AuthService.checkEmail(email);

      const { found, personID, type } = user;

      if (!found || !personID || !type) {
        // Does not exist in Cognito. Check Dynamo.
        try {
          const response = (await client.graphql({
            query: customQueries.peopleByEmail,
            variables: { email },
          })) as GraphQLResult<PeopleByEmailQuery>;
          const people = [
            ...(response.data?.employersByEmail?.items || []),
            ...(response.data?.nationalsByEmail?.items || []),
            ...(response.data?.teamByEmail?.items || []),
          ];

          return people.length > 0 ? people[0] : null;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err);

          throw new Error();
        }
      }

      switch (type) {
        case 'Employer':
          query = queries.getEmployer;

          break;

        case 'ForeignNational':
          query = getForeignNational;

          break;

        case 'TeamMember':
          query = queries.getTeamMember;

          break;

        default:
          return null;
      }

      const person = ((await client.graphql({
        query: query,
        variables: { id: personID },
      })) as any).data[`get${type}`];

      return person;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  public getBySlug = async (slug: string): Promise<Person | null> => {
    try {
      const response = (await client.graphql({
        query: customQueries.peopleBySlug,
        variables: { slug },
      })) as GraphQLResult<PeopleBySlugQuery>;
      const people = [
        ...(response.data?.employersBySlug?.items || []),
        ...(response.data?.nationalsBySlug?.items || []),
        ...(response.data?.teamBySlug?.items || []),
      ];

      return people[0] || null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  // For when we know a person’s slug and type and can
  // now get the full profile.
  public getByPerson = async (person: Person): Promise<Person> => {
    let response;

    const type = person.__typename || person.type;
    const { id } = person;

    if (!type || !id) {
      throw new Error();
    }

    switch (type) {
      case 'Employer':
        try {
          response = (await client.graphql({
            query: getEmployer,
            variables: { id },
          })) as GraphQLResult<ContainerGetEmployerQuery>;
        } catch (err:any) {
          if (err?.data?.getEmployer) {
            response = err;
          }
        }

        if (response?.data?.getEmployer) {
          return response.data.getEmployer;
        }

        break;

      case 'ForeignNational':
        try {
          response = (await client.graphql({
            query: getForeignNational,
            variables: { id },
          })) as GraphQLResult<ContainerGetForeignNationalQuery>;

          if (response.data?.getForeignNational?.fileRequirementsManipulated !== 'true') {
            const fileRequirements = nationalFileRequirements;
            const fileRequirementsManipulated = true;

            const variables = { input: { id, fileRequirements, fileRequirementsManipulated } };

            const updateFN = await client.graphql({
              query: updateForeignNational,
              variables: variables,
            }) as any;

            response = updateFN;
          }
        } catch (err: any) {
          if (err?.data?.getForeignNational) {
            response = err;
          }
        }

        if (response?.data?.getForeignNational) {
          // There is a bug with the resolvers that is affecting
          // requests FN users make for other FNs. Namely, the resolver
          // is changing the ID value to the person_id of the requester.
          // Until this can be resolved, we ensure the correct ID here.
          // As of CLI 8.4.0, this bug is not fixed.
          response.data.getForeignNational.id = id;

          return response.data.getForeignNational;
        }

        if (response?.data?.updateForeignNational) {
          response.data.updateForeignNational.id = id;

          return response.data.updateForeignNational;
        }

        break;

      case 'TeamMember':
        try {
          response = (await client.graphql({
            query: getTeamMember,
            variables: { id },
          })) as GraphQLResult<ContainerGetTeamMemberQuery>;
        } catch (err: any) {
          if (err?.data?.getTeamMember) {
            response = err;
          }
        }

        if (response?.data?.getTeamMember) {
          return response.data.getTeamMember;
        }

        break;

      default:
        throw new Error();
    }

    throw new Error();
  };

  public getCurrent = async (): Promise<Person | null> => {
    const loggedInUser = await AuthService.getAuthUser();
    if (!loggedInUser) return null;
    const userAttributes = await fetchUserAttributes();
    const personType = userAttributes['custom:type'];
    const personID = userAttributes['custom:person_id'];

    try {
      let query = '';

      if (!personType || !personID) {
        return null;
      }

      switch (personType) {
        case 'Employer':
          query = customQueries.getCurrentEmployer;

          break;

        case 'ForeignNational':
          query = getForeignNational;

          break;

        case 'TeamMember':
          query = customQueries.getCurrentTeamMember;

          break;

        default:
          throw new Error();
      }

      const personResponse = await client.graphql({
        query: query,
        variables: { id: personID },
      }) as any;

      const person = personResponse.data[`get${personType}`];

      if (person) {
        return person;
      }

      return null;
    } catch (err: any) {
      if (err.data && err.data[`get${personType}`]) {
        return err.data[`get${personType}`];
      }

      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  fullname = (
    person?: Person, personalize?: boolean, withMiddle?: boolean,
  ): string => {
    if (!person) {
      return '—';
    }

    const { firstname, lastname, middlename } = person;

    if (personalize && person.id === this.getPersonID()) {
      return 'you';
    }

    if (!firstname && !lastname) {
      return '[ No name ]';
    }

    return `${firstname || ''}${withMiddle && middlename ? ` ${middlename}` : ''}${lastname ? ` ${lastname}` : ''}`;
  };

  getLabel = (fieldName: string): string => labels[fieldName] || '';

  // Used by TaskService.getNextTasks for the "My Tasks" list widget — kept as the
  // filtered listTasks scan since that widget needs full Task objects, not just a count.
  getTaskCountQuery = async (assignedToID:string, nextToken?:string) => await client.graphql({
    query: customListTaskQueryForCount,
    variables: {
      filter: {
        ...(this.person?.position === Position.LEGAL_ASSISTANT ? {
          or: [
            { assignedToID: { eq: Position.LEGAL_ASSISTANT } },
            { assignedToID: { eq: assignedToID } },
          ],
        } : { assignedToID: { eq: assignedToID } }),
        state: {
          eq: 'TODO',
        },
        caseState: {
          eq: 'OPEN',
        },
      },

      nextToken,
      limit: 1000,
    },
  }) as GraphQLResult<ListTasksQuery>;

  getPersonTaskCountQuery = async (nextToken?: string) => await client.graphql({
    query: personTaskCountQuery(this.personType as PersonType),
    variables: {
      id: this.personID,
      nextToken,
    },
  }) as GraphQLResult<PersonTaskCountQueryResult>;

  // Pooled tasks assigned to the "Legal Assistant" queue are stored with the literal
  // sentinel `assignedToID: 'LEGAL_ASSISTANT'`, not a real person's id, so they can't be
  // reached via the indexed tasks(...) relation above and still need a filtered scan.
  getPooledLegalAssistantTaskCountQuery = async (nextToken?: string) => await client.graphql({
    query: customListTaskQueryForCount,
    variables: {
      filter: {
        assignedToID: { eq: Position.LEGAL_ASSISTANT },
        state: { eq: 'TODO' },
        caseState: { eq: 'OPEN' },
      },
      nextToken,
      limit: 1000,
    },
  }) as GraphQLResult<ListTasksQuery>;

  getTaskCount = async (): Promise<number> => {
    let tasks = 0;
    let token: string | null | undefined;

    do {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await this.getPersonTaskCountQuery(token || undefined);
      const { items = [], nextToken = null } = data?.[`get${this.personType}`]?.tasks || {};

      tasks += (items || []).length;
      token = nextToken;
    } while (token);

    if (this.person?.position === Position.LEGAL_ASSISTANT) {
      // listTasks is a filtered DynamoDB scan, so pages can come back empty with a
      // non-null nextToken. Cap pagination so a sparse match on a large Tasks table
      // can't walk the whole table and stall the snapshot (see the 99-count cap too).
      const MAX_PAGES = 10;
      let pages = 0;
      let scanToken: string | undefined;
      let hasMore = true;

      while (hasMore && tasks < 99 && pages < MAX_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const { data: pooledData } = await this.getPooledLegalAssistantTaskCountQuery(scanToken);
        const { items: pooledItems = [], nextToken: pooledNextToken = null } = pooledData?.listTasks || {};

        tasks += (pooledItems || []).length;
        pages += 1;
        hasMore = !!pooledNextToken;
        scanToken = pooledNextToken || undefined;
      }
    }

    return tasks;
  };

  loadRelatives = async (isRelative: Array<FamilyRelationship | null>) => {
    const relativeArray: ForeignNational[] = [];

    await Promise.all(
      isRelative?.map(async (relative: FamilyRelationship | null) => {
        const getFN: ForeignNational = await NationalService.getForeignNational(relative?.target?.id || '') as ForeignNational;

        if (getFN) {
          relativeArray.push(getFN);
        }

        return getFN;
      }),
    );

    return relativeArray;
  };

  getSnapshotCount = async (person: ForeignNational, role: Role): Promise<SnapshotCount> => {
    let cases = 0;
    let processRequests = 0;

    if (this.personType === PersonType.Employer) {
      const response = await client.graphql({
        query: getEmployerSnapshotCount,
        variables: { id: this.personID },
      }) as GraphQLResult<GetEmployerSnapshotCountQuery>;

      processRequests = (
        response.data?.getEmployer?.processRequestAssignments?.items || []
      ).length;

      const casesAsContributor = response
        .data?.getEmployer?.contributorCases?.items
        .filter(notNullFilter)
        .map((item) => item!.case) || [];

      const casesAsSponsor = response.data?.getEmployer?.sponsoredCases?.items
        .filter(notNullFilter) || [];

      const allCases = uniquesByKey([...casesAsContributor, ...casesAsSponsor]);

      cases = allCases.length;

      return { cases, processRequests, tasks: await this.getTaskCount() };
    }

    if (this.personType === PersonType.ForeignNational) {
      const response = await client.graphql({
        query: getForeignNationalSnapshotCount,
        variables: { id: this.personID },
      }) as GraphQLResult<GetForeignNationalSnapshotCountQuery>;

      const relativesAdmin: Array<FamilyRelationship> = isRelativeAdmin(response.data?.getForeignNational as ForeignNational);

      let total = 0;
      let fill = 0;

      if (relativesAdmin) {
        const relativesArray = await this.loadRelatives(relativesAdmin);

        if (relativesArray.length) {
          relativesArray.map((relative) => {
            const [todo, outOf] = NationalService.onboardingSectionsDone(
              relative.onboardingSections || undefined,
            );

            total += outOf;
            fill += todo;

            return relative;
          });
        }
      }

      const [todo, outOf] = NationalService.onboardingSectionsDone(
        response.data?.getForeignNational?.onboardingSections || undefined,
      );

      const CaseFileTypesArray: any = [];

      response.data?.getForeignNational?.cases?.items.map((fileType) => {
        fileType?.fileRequirements.filter(({ type }) => {
          const foundedFile = FILE_TYPES.find((doc) => (doc.type === type && doc.defaultStorage === 'national'));

          if (foundedFile && foundedFile.type !== FileType.OTHER && foundedFile.type !== FileType.OTHER_NOTICES) {
            CaseFileTypesArray.push(foundedFile);
          }

          return type;
        });

        return fileType;
      });

      const onboardingSections = ((total + outOf) - (todo + fill));
      const filesArray: Array<DocumentType | undefined> = [];

      const files = response.data?.getForeignNational?.files?.items || [];

      files?.map((file) => {
        const foundedFile = FILE_TYPES.find((doc) => (doc.type === file?.fileType && doc.defaultStorage === 'national'));

        if (foundedFile) {
          filesArray.push(foundedFile);
        }

        return file;
      });

      const fileArray: Array<DocumentType | undefined> = [];
      const foundedFile: Array<DocumentType | undefined> = [];

      const categories = [
        {
          category: FileCategory.EDUCATION,
        },
        {
          category: FileCategory.EMPLOYMENT,
        },
        {
          category: FileCategory.PERSONAL_IDENTIFICATION,
        },
        {
          category: FileCategory.US_IMMIGRATION,
        },
        {
          category: FileCategory.GOVERNMENT_NOTICES,
        },
        {
          category: FileCategory.FORMS_LETTERS,
        },
        {
          category: FileCategory.OTHER,
        },
        {
          category: FileCategory.SIGNATURE_PAGES,
        },
        {
          category: FileCategory.APPLICATIONS_APPROVALS,
        },
        {
          category: FileCategory.EXPERIENCE_LETTER,
        },
        {
          category: FileCategory.PUBLIC_ACCESS_FILE,
        },
      ];

      categories.map((category) => (

        uniquesByKey(fileRequirementsMatchingCategory(
          response.data?.getForeignNational?.fileRequirements || [], category.category,
        ), 'type')
          .map((fileType) => {
            const { type } = fileType;
            const file = FILE_TYPES.find((doc) => (doc.type === type && doc.defaultStorage === 'national'
            && (!doc?.visibility || doc?.visibility?.includes(role))));
            if (file) {
              foundedFile.push(file);
            }

            return fileType;
          })
      ));

      foundedFile.map((file: any) => {
        if (person) {
          if (file) {
            if (filesArray?.length) {
              filesArray?.map((fileData) => {
                if (fileData?.type === file?.type) {
                  fileArray.push(file);
                }

                return fileData;
              });
            }
          }
        }

        return file;
      });

      const notHiddenFileTypes: Array<FileRequirement> | any = response.data?.getForeignNational?.fileRequirements?.filter((file) => (file.hidden === false && file.type !== 'OTHER' && file.type !== 'OTHER_NOTICES'));

      const notHidden: FileRequirement[] = notHiddenFileTypes?.map((file: FileRequirement) => (FILE_TYPES.find((doc) => doc?.type === file?.type)));

      const finalFileArray = [...CaseFileTypesArray, ...fileArray, ...notHidden];

      const uniqueFileTypeArray: FileRequirement[] = finalFileArray.filter((value, index) => finalFileArray?.indexOf(value) === index);

      const finalFilesArray = [];

      uniqueFileTypeArray.filter((uniqueOpenFile) => {
        const foundedFiles = FILE_TYPES.find((doc) => (doc?.type === uniqueOpenFile?.type
          && doc.defaultStorage === 'national') && (!doc?.visibility || doc?.visibility?.includes(role)));
        if (foundedFiles) {
          finalFilesArray.push({ ...uniqueOpenFile, foundedFiles });
        }

        return foundedFile;
      });

      const requirementCount = finalFilesArray.length || 0;

      const documentsCompleted = uniqueFileTypeArray.filter(
        (requirement) => filesArray.some(
          (doc) => (doc?.type === requirement?.type
            && doc.defaultStorage === 'national') && (!doc?.visibility || doc?.visibility?.includes(role)),
        ),
      ).length;

      const documentCount = requirementCount - documentsCompleted;
      const documents = documentCount > 0 ? documentCount : 0;

      return { documents, onboardingSections, tasks: await this.getTaskCount() };
    }

    if (this.personType === PersonType.TeamMember) {
      const response = await getCacheWithExpiration(
        'snapshot-data',
        async () => await client.graphql({
          query: getTeamMemberSnapshotCount,
          variables: {
            id: this.personID, id2: this.personID,
          },
        }) as GraphQLResult<GetTeamMemberSnapshotCountQuery>,
        5 * 60 * 1000, // Cache for 5 minutes
      );

      processRequests = (
        response.data?.getTeamMember?.processRequestAssignments?.items || []
      ).length;

      cases = uniquesByKey([
        ...(response.data?.getTeamMember?.attorneyCases?.items || []),
        ...(response.data?.getTeamMember?.caseManagerCases?.items || []),
        ...(response.data?.getTeamMember?.contributorCases?.items
          .filter(notNullFilter) || []).map((item: any) => item!.case),
      ]).filter(notNullFilter).length;

      return { cases, processRequests, tasks: await this.getTaskCount() };
    }

    return {};
  };

  /**
   *
   * @returns {string} Dynamo ID of the current user
   */
  getPersonID = () => this.personID;

  /**
   *
   * @returns {PersonType} Type of current user
   */
  getPersonType = () => this.personType;

  initials = (person: Person): string => {
    if (!person?.firstname && !person?.lastname) {
      return '-';
    }

    return `${person.firstname?.slice(0, 1) || ''}${
      person.lastname?.slice(0, 1) || ''
    }`;
  };

  link = (person?: Person): string => (person
    ? `${ROUTES.people}/${person.slug}` : ROUTES.people);

  /**
   *
   * @param person The person whose name we should display
   * @param personalize Whether to use “you” when person matches current user
   * @param withProfile Whether to append with ”’s profile”
   * @returns
   */
  linkFullName = (
    person?: Person, personalize?: boolean, withProfile?: boolean,
  ) => {
    if (!person) {
      return '—';
    }

    const personID = this.getPersonID();

    if (withProfile) {
      if (personalize && personID === person.id) {
        return <Link to={this.link(person)}>your profile</Link>;
      }

      return (
        <Link to={this.link(person)}>
          {`${possessive(this.fullname(person))} profile`}
        </Link>
      );
    }

    return (
      <Link to={this.link(person)}>
        {this.fullname(person, personalize, true)}
      </Link>
    );
  };

  getLowerNames = (
    person: Person,
  ): {
    lowerFirst: string | null;
    lowerLast: string | null;
    lowerFull: string | null;
  } => {
    const lowerFirst = person.firstname ? lower(person.firstname) : '-';
    const lowerLast = person.lastname ? lower(person.lastname) : '-';
    const lowerFull = lowerFirst || lowerLast
      ? `${`${lowerLast} ` || ''}${lowerFirst || ''}`
      : '-';

    return { lowerFirst, lowerLast, lowerFull };
  };

  /**
   * Recursively generates unique slug for a person
   * by checking for a slugified version of a name.
   * If slug already exists, tries again with '-2'
   * appended to name, then '-3', '-4', etc.
   * @param {string} base
   * @param {number} counter
   * @returns {string}
   */
  generateSlug = async (base: string, counter: number = 1): Promise<string> => {
    let isTaken = false;
    const slug = slugify(
      `${base}${counter > 1 ? ` ${counter}` : ''}`,
      SLUGIFY_OPTIONS,
    );
    isTaken = (await this.getBySlug(slug)) !== null;

    return isTaken ? this.generateSlug(base, counter + 1) : slug;
  };

  personTypes = [
    PersonType.Employer,
    PersonType.ForeignNational,
    PersonType.TeamMember,
  ];

  /**
   *
   * @param personID Dynamo ID of current user
   */
  setPersonID = (personID: string) => {
    this.personID = personID;
  };

  /**
   *
   * @param person current user
   */
  setPerson = (person: Person) => {
    this.person = person;
  };

  /**
   *
   * @returns person
   */
  getPerson = () => this.person;

  /**
   *
   * @param personType Type of current user
   */
  setPersonType = (personType?: PersonType) => {
    this.personType = personType;
  };

  roleToType = (role: Role): PersonType => {
    switch (role) {
      case Role.FOREIGNNATIONAL:
        return PersonType.ForeignNational;

      case Role.EMPLOYER:
        return PersonType.Employer;

      case Role.TEAMMEMBER:
      case Role.ADMIN:
        return PersonType.TeamMember;

      default:
        throw new Error();
    }
  };

  // This performs a check only on the database, not Cognito.
  // It's appropriate only for users who do not have a Cognito profile yet.
  public emailExistsInDatabase = async (email: string): Promise<boolean> => {
    try {
      const response = (await client.graphql({
        query: customQueries.peopleByEmail,
        variables: { email },
      })) as GraphQLResult<PeopleByEmailQuery>;
      const people = [
        ...(response.data?.employersByEmail?.items || []),
        ...(response.data?.nationalsByEmail?.items || []),
        ...(response.data?.teamByEmail?.items || []),
      ];

      return people.length > 0;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  // Note: This only updates the email in the Dynamo database.
  // If the user already has an Cognito account, the email
  // should be updated there instead.
  updateEmail = async (person: Person, newEmail: string) => {
    if (await this.emailExistsInDatabase(newEmail)) {
      throw new Error(DICTIONARY.EmailTaken);
    }

    let mutation;
    switch (person.type) {
      case 'Employer':
        mutation = updateEmployer;

        break;

      case 'ForeignNational':
        mutation = updateForeignNational;

        break;

      case 'TeamMember':
        mutation = updateTeamMember;

        break;

      default:
        throw new Error();
    }

    return client.graphql({
      query: mutation,
      variables: { input: { id: person.id, email: newEmail } },
    });
  };

  updateHasLoggedIn = async (person: Person) => {
    if (person.hasLoggedIn) {
      return;
    }

    const mutation = /* GraphQL */ `
      mutation SetHasLoggedIn($id: ID!, $hasLoggedIn: Boolean) {
        update${person.type}(input: {id: $id, hasLoggedIn: $hasLoggedIn}) {
          id
        }
      }`;

    await client.graphql({
      query: mutation,
      variables: { id: person.id, hasLoggedIn: true },
    });
  };
}

export default new PersonService();
